<template>
  <div class="template-page">
    <el-card class="tool-card" shadow="never">
      <div class="tool-row">
        <div class="tool-left">
          <span class="tool-title">协议模板</span>
          <span class="tool-sub">富文本正文含 {$变量} 占位，生成协议时渲染为快照</span>
        </div>
        <el-button type="primary" icon="el-icon-plus" @click="openEdit(null)">新增模板</el-button>
      </div>
    </el-card>

    <el-card class="table-card" shadow="never">
      <el-table
        v-loading="loading"
        :data="list"
        :header-cell-style="{ background: '#F7F9FC', color: '#18233C', fontWeight: 600 }"
        empty-text="暂无协议模板"
      >
        <el-table-column label="模板名称" min-width="170">
          <template slot-scope="{ row }">
            <div class="tmpl-name">{{ row.name }}</div>
            <div v-if="row.subject" class="tmpl-subject">主体：{{ row.subject }}</div>
          </template>
        </el-table-column>
        <el-table-column label="已用次数" min-width="90" align="center">
          <template slot-scope="{ row }">
            <span class="count-num">{{ row.use_count }}</span>
          </template>
        </el-table-column>
        <el-table-column label="变量参数" min-width="240">
          <template slot-scope="{ row }">
            <div class="params-wrap">
              <el-tag v-for="p in splitParams(row.params)" :key="p" size="mini" type="info" effect="plain" style="margin: 2px;">{{ p }}</el-tag>
              <span v-if="!row.params" class="no-params">—</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="状态" min-width="80" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="parseInt(row.status) === 1 ? 'success' : 'info'" size="small">
              {{ parseInt(row.status) === 1 ? '启用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openEdit(row)">编辑</el-button>
            <el-button type="text" size="small" class="del-btn" @click="doDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 新增/编辑模板弹窗 -->
    <el-dialog :title="editId ? '编辑模板' : '新增模板'" :visible.sync="editVisible" width="900px" top="3vh" :close-on-click-modal="false">
      <el-form ref="form" :model="form" :rules="rules" label-width="90px" size="small">
        <div class="form-grid">
          <el-form-item label="模板名称" prop="name">
            <el-input v-model="form.name" :maxlength="100" placeholder="请输入协议名称" />
          </el-form-item>
          <el-form-item label="主体名称">
            <el-input v-model="form.subject" :maxlength="100" placeholder="协议甲方的名称，如：XXX公司" />
          </el-form-item>
          <el-form-item label="排序">
            <el-input-number v-model="form.weigh" :min="0" :max="9999" style="width: 140px" />
          </el-form-item>
          <el-form-item label="状态">
            <el-switch v-model="form.status" active-text="启用" inactive-text="停用" />
          </el-form-item>
        </div>

        <el-form-item label="印章图">
          <div class="seal-row">
            <el-upload
              class="seal-uploader"
              action="/api/upload/index"
              name="file"
              :headers="uploadHeaders"
              :show-file-list="false"
              :on-success="onSealSuccess"
              :on-error="() => $message.error('印章上传失败')"
              accept="image/*"
            >
              <div class="seal-box">
                <img v-if="form.seal" :src="form.seal" class="seal-preview" />
                <i v-else class="el-icon-plus seal-upload-icon"></i>
              </div>
            </el-upload>
            <div class="seal-actions">
              <el-button v-if="form.seal" type="text" size="small" class="del-btn" @click="form.seal = ''">移除</el-button>
              <span class="seal-tip">没有印章？点击 <a href="https://tools.kalvinbg.cn/convenience/seal" target="_blank" rel="noopener" class="seal-link">前往制作</a>（在线生成印章图片）</span>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="渲染变量">
          <div class="var-tags">
            <el-tag
              v-for="v in builtinVars"
              :key="v"
              size="small"
              type="success"
              effect="light"
              class="var-tag"
              @click.native="insertVar(v)"
            >{$ {{ v }} }</el-tag>
          </div>
          <div class="content-hint">点击变量插入正文光标处，生成协议时自动替换为实际值</div>
        </el-form-item>

        <el-form-item label="协议内容" prop="content">
          <div class="ql-wrap">
            <div ref="editor" class="ql-editor-host"></div>
          </div>
        </el-form-item>

        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="2" :maxlength="255" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="closeEdit">取 消</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="doSave">保 存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { templateList, templateSave, templateDelete } from '@/api/agreement'
import { API_BASE } from '@/config'

const ASSET_BASE = API_BASE.replace(/\/api\/?$/, '')

// 动态注入 TinyMCE 7 到全局（避开 webpack 打包），用 window.tinymce
let tinymceLoaded = false
function loadTinyMCE() {
  return new Promise((resolve, reject) => {
    if (window.tinymce) { tinymceLoaded = true; resolve(window.tinymce); return }
    if (tinymceLoaded) { resolve(window.tinymce); return }
    const script = document.createElement('script')
    script.src = '/tinymce/tinymce.min.js'
    script.onload = () => { tinymceLoaded = true; resolve(window.tinymce) }
    script.onerror = () => reject(new Error('TinyMCE 加载失败'))
    document.head.appendChild(script)
  })
}

export default {
  name: 'TemplateList',
  data() {
    return {
      list: [],
      loading: false,
      editVisible: false,
      editId: 0,
      saving: false,
      builtinVars: ['乙方姓名', '乙方电话', '订单号', '身份证号', 'qq', '邮箱', '订单备注', '商品名', '商品原价', '商品价格', '商品价格大写', '商品内容', '商品备注', '成交日期', '录入时间', '当天日期', '退款金额', '退款平台', '第三方单号', '退款备注', '量账号', '机构名', '主体名称', '所在地区', '金额大写', '退款渠道', '报名时间', '备注'],
      editor: null,
      form: { name: '', subject: '', weigh: 0, content: '', params: '', seal: '', status: true, remark: '' },
      rules: {
        name: [{ required: true, message: '请填写模板名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    uploadHeaders() {
      const token = this.$store.state.token || localStorage.getItem('crm_token') || ''
      return { Token: token }
    }
  },
  created() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      templateList()
        .then(data => { this.list = data.list || [] })
        .catch(() => { this.list = [] })
        .finally(() => { this.loading = false })
    },
    splitParams(params) {
      if (!params) return []
      return String(params).split(',').filter(Boolean)
    },
    /* ---- TinyMCE 7 富文本编辑器 ---- */
    initTinyMCE(content) {
      // 等待 el-dialog 挂载完成后再初始化
      setTimeout(() => {
        const host = this.$refs.editor
        if (!host) return
        loadTinyMCE()
          .then(tinymce => {
            if (!tinymce) throw new Error('TinyMCE 未就绪')
            // 已有实例：先销毁再重建（避免重复 init 报错）
            if (this.editor) {
              try { this.editor.destroy() } catch (e) {}
              this.editor = null
            }
            tinymce.init({
              target: host,
              height: 420,
              menubar: false,
              branding: false,
              promotion: false,
              language: 'zh_CN',
              language_url: '/tinymce/langs/zh_CN.js',
              // 使用 GPL 开源授权，消除 evaluation mode 提示
              license_key: 'gpl',
              plugins: 'table image link lists advlist code fullscreen',
              toolbar: [
                'undo redo | blocks | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright | bullist numlist',
                'tableinsertdialog | tableinsertrowbefore tableinsertrowafter | tabledeleterow | tableinsertcolbefore tableinsertcolafter | tabledeletecol | tablemergecells tablesplitcells tableprops tabledelete | image link | code fullscreen'
              ],
              // 保留完整 HTML/CSS，便于协议模板自定义样式
              valid_elements: '*[*]',
              valid_styles: { '*': '*' },
              extended_valid_elements: '*[*]',
              // 图片上传：走 /api/upload/index
              file_picker_types: 'image',
              file_picker_callback: (cb, value, meta) => {
                const input = document.createElement('input')
                input.setAttribute('type', 'file')
                input.setAttribute('accept', 'image/*')
                input.onchange = () => {
                  const file = input.files && input.files[0]
                  if (!file) return
                  const fd = new FormData()
                  fd.append('file', file)
                  const token = this.$store.state.token || localStorage.getItem('crm_token') || ''
                  fetch('/api/upload/index', { method: 'POST', headers: { Token: token }, body: fd })
                    .then(res => res.json())
                    .then(json => {
                      if (json && json.code === 1 && json.data && json.data.url) {
                        cb(this.resolveAsset(json.data.url), { title: file.name })
                      } else {
                        this.$message.error((json && json.msg) || '图片上传失败')
                      }
                    })
                    .catch(() => this.$message.error('图片上传失败'))
                }
                input.click()
              },
              setup: (ed) => {
                ed.on('init', () => {
                  this.editor = ed
                  if (content) ed.setContent(content)
                  this.syncContent()
                })
                ed.on('change keyup', () => this.syncContent())
              }
            })
          })
          .catch(err => console.error('TinyMCE 初始化失败', err))
      }, 300)
    },
    insertVar(name) {
      if (!this.editor) return
      this.editor.focus()
      this.editor.insertContent('{$' + name + '}')
      this.syncContent()
    },
    resolveAsset(url) {
      if (!url) return ''
      if (/^https?:\/\//.test(url)) return url
      if (/^data:/.test(url)) return url
      return ASSET_BASE + url
    },
    syncContent() {
      this.form.content = this.editor ? this.editor.getContent() : this.form.content
    },
    /* ---- 弹窗 ---- */
    openEdit(row) {
      if (row) {
        this.editId = row.id
        this.form = {
          name: row.name, subject: row.subject || '', weigh: row.weigh || 0,
          content: '', params: row.params || '', seal: row.seal || '',
          status: parseInt(row.status) === 1, remark: row.remark || ''
        }
        this.loadDetail(row.id)
      } else {
        this.editId = 0
        this.form = { name: '', subject: '', weigh: 0, content: '', params: '', seal: '', status: true, remark: '' }
      }
      this.editVisible = true
      this.initTinyMCE(this.form.content)
    },
    loadDetail(id) {
      import('@/api/agreement').then(({ templateDetail }) => {
        templateDetail(id).then(data => {
          if (!data) return
          this.form.content = data.content || ''
          this.form.subject = data.subject || ''
          this.form.weigh = data.weigh || 0
          this.form.params = data.params || this.form.params
          this.form.seal = data.seal || this.form.seal
          this.form.remark = data.remark || this.form.remark
          this.form.status = data.status ? parseInt(data.status) === 1 : this.form.status
          // 详情加载完再渲染到 TinyMCE
          this.initTinyMCE(data.content || '')
        }).catch(() => {})
      })
    },
    closeEdit() {
      this.editVisible = false
      if (this.editor) {
        try { this.editor.destroy() } catch (e) {}
        this.editor = null
      }
    },
    onSealSuccess(res) {
      if (res && res.data && res.data.url) {
        this.form.seal = res.data.url
        this.$message.success('印章上传成功')
      } else {
        this.$message.error('印章上传失败')
      }
    },
    doSave() {
      this.syncContent()
      this.$refs.form.validate(valid => {
        if (!valid) return
        if (!this.form.content || !String(this.form.content).replace(/<[^>]+>/g, '').trim()) {
          this.$message.warning('请填写协议正文')
          return
        }
        this.saving = true
        templateSave({
          id: this.editId,
          name: this.form.name,
          subject: (this.form.subject || '').trim(),
          weigh: this.form.weigh || 0,
          content: this.form.content,
          params: (this.form.params || '').trim(),
          seal: this.form.seal,
          status: this.form.status ? 1 : 0,
          remark: (this.form.remark || '').trim()
        })
          .then(() => {
            this.$message.success(this.editId ? '修改成功' : '新增成功')
            this.closeEdit()
            this.load()
          })
          .catch(() => {})
          .finally(() => { this.saving = false })
      })
    },
    doDelete(row) {
      this.$confirm(`确认删除模板「${row.name}」？`, '删除模板', {
        confirmButtonText: '删 除',
        cancelButtonText: '取 消',
        type: 'warning'
      }).then(() => {
        templateDelete(row.id)
          .then(() => {
            this.$message.success('删除成功')
            this.load()
          })
          .catch(() => {})
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.template-page { padding: 0; }

.tool-card, .table-card {
  margin-bottom: 14px;
  border-radius: 10px;
  border: 1px solid #E5E9F2;
}

.tool-row { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.tool-left { display: flex; align-items: baseline; gap: 12px; }
.tool-title { font-size: 15px; font-weight: 700; color: #18233C; }
.tool-sub { font-size: 12px; color: #8A94AD; }

.tmpl-name { font-size: 13px; font-weight: 600; color: #18233C; }
.tmpl-subject { font-size: 11px; color: #8A94AD; margin-top: 2px; }
.count-num { font-size: 14px; font-weight: 600; color: #3668FC; }

.params-wrap { display: flex; flex-wrap: wrap; gap: 2px; padding: 4px 0; }
.no-params { color: #B4BDCF; font-size: 12px; }
.del-btn { color: #E5484D; }

/* 表单两列网格 */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 16px;
}

/* 渲染变量标签 */
.content-hint { margin-top: 6px; font-size: 12px; color: #8A94AD; }
.var-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.var-tag { cursor: pointer; }
.var-tag:hover { opacity: 0.85; }

/* 印章 */
.seal-row { display: flex; align-items: flex-start; gap: 14px; }
.seal-uploader { display: inline-block; }
.seal-box {
  width: 96px; height: 96px;
  border: 1px dashed #C3CADB; border-radius: 8px;
  cursor: pointer; overflow: hidden;
  display: inline-flex; align-items: center; justify-content: center;
  background: #FAFBFF; transition: border-color 0.2s;
}
.seal-uploader :deep(.el-upload):hover .seal-box { border-color: #3668FC; }
.seal-upload-icon { font-size: 22px; color: #8A94AD; }
.seal-preview { width: 100%; height: 100%; object-fit: contain; display: block; }
.seal-actions { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; }
.seal-tip { font-size: 12px; color: #8A94AD; }
.seal-link { color: #3668FC; font-weight: 600; }

/* TinyMCE 富文本 */
.ql-wrap {
  border: 1px solid #DCDFE6;
  border-radius: 6px;
  overflow: hidden;
}
.ql-wrap :deep(.tox-tinymce) { border: none !important; }
.ql-wrap :deep(.tox-editor-container) { min-height: 420px; }

/* 品牌色 */
::v-deep .el-button--primary { background: #3668FC; border-color: #3668FC; }
::v-deep .el-button--primary:hover,
::v-deep .el-button--primary:focus { background: #5B8DFF; border-color: #5B8DFF; }
::v-deep .el-table__row:hover > td { background: #F5F8FF !important; }
::v-deep .el-dialog { border-radius: 12px; }
</style>

<style>
/* TinyMCE 的对话框/下拉/菜单等浮层可能渲染在 body 顶层或编辑器根上，
   统一提升它们与编辑器本身的层级，避免被所在的 el-dialog 遮住 */
.tox-tinymce,
.tox-tinymce-aux,
.tox .tox-dialog-wrap,
.tox .tox-menu,
.tox .tox-dialog {
  z-index: 200000 !important;
}
</style>